import { getCsrfToken } from "next-auth/react";
import Link from "next/link";
import { useRouter } from "next/router";
import { GetServerSideProps, InferGetServerSidePropsType } from "next/types";
import React from "react";
import { Container } from "@/components/Container";
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Mdx,
  Stack,
} from "@/components/ui";

export const getServerSideProps: GetServerSideProps<{
  csrfToken: string;
}> = async (context) => {
  const csrfToken = await getCsrfToken(context);

  if (!csrfToken) {
    return {
      redirect: {
        destination: "/orders",
        permanent: false,
      },
    };
  }

  return {
    props: {
      csrfToken,
    },
  };
};

function getErrorMessage(
  error: "CredentialsSignin" | "SessionRequired" | string,
): string {
  switch (error) {
    case "CredentialsSignin": {
      return "Email or order with the provided ID could not be found";
    }
    case "SessionRequired": {
      return "You must be signed in to access this page";
    }
    default: {
      return error;
    }
  }
}

export default function SignInPage({
  csrfToken,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter();
  const { query } = router;
  const { error, success, email: initialEmail } = query;
  const errors = (Array.isArray(error) ? error : [error]).filter(
    Boolean,
  ) as ReadonlyArray<string>;
  const [email, setEmail] = React.useState(initialEmail || "");
  return (
    <Container>
      <Mdx.h1>My orders</Mdx.h1>

      <Box css={{ position: "relative" }}>
        <Box
          aria-hidden="true"
          css={{
            position: "absolute",
            top: 0,
            left: "45%",
            fontSize: 6,
            textAlign: "center",
          }}
        >
          📦
        </Box>
        <Stack
          as="form"
          action="/api/auth/callback/credentials"
          method="POST"
          gap={3}
          css={{
            mt: 4,
            p: 3,
            background: "muted",
            borderColor: "border",
            borderRadius: "medium",
            border: "thin",
          }}
        >
          {errors.map((error) => (
            <Alert key={error} variant="error">
              {getErrorMessage(error)}
            </Alert>
          ))}
          {success === "forgot-id" && (
            <Alert variant="primary">✔ We sent an email to {email}...</Alert>
          )}
          <input name="csrfToken" type="hidden" defaultValue={csrfToken} />
          <FormField id="email" label="Email" required>
            <Input
              type="email"
              id="email"
              name="email"
              size="large"
              value={email}
              onChange={(event) => setEmail(event.target.value.trim())}
              data-splitbee-property="email"
              placeholder="john@appleseed.com"
              // @ts-expect-error types are wrong
              autofillBackgroundColor="comment"
              autoFocus={!initialEmail}
              required
            />
          </FormField>
          <FormField
            id="order_id"
            label="Order Number"
            required
            explanation={
              <Link
                href={`/orders/forgot?email=${email}`}
                passHref
                legacyBehavior
              >
                <Mdx.a css={{ fontSize: 1 }}>Forgot Order Number?</Mdx.a>
              </Link>
            }
          >
            <Input
              type="text"
              id="order_id"
              name="order_id"
              size="large"
              data-splitbee-property="order_id"
              placeholder="8 digits number"
              autoFocus={!!initialEmail}
              required
            />
          </FormField>
          <Button type="submit">Sign in</Button>
        </Stack>
      </Box>
    </Container>
  );
}
